// src/pages/EmployerTimeTracking.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { auth, db } from "../firebase/config";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  where,
  getDocs,
  deleteField,
} from "firebase/firestore";
import NoDataIllustration from "../tools/assets/undraw_time-management_4ss6.svg";

import "../styles/EmployerTimeTracking.css";
import { SlidersHorizontal } from "lucide-react";

import { isEntryLocked } from "../utils/timesheets";
import {
  getTimeTrackingConfigured,
  setTimeTrackingConfigured,
} from "../utils/firstRunFlags";

export default function EmployerTimeTracking() {
  const [timeEntries, setTimeEntries] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");

  // Multi-location / worksites
  const [
    companyMultiLocationEnabled,
    setCompanyMultiLocationEnabled,
  ] = useState(false);
  const [locations, setLocations] = useState([]); // companies/{companyId}/locations
  const [filterLocationId, setFilterLocationId] = useState("all"); // worksite filter

  // employer identity for audit trail
  const [employerName, setEmployerName] = useState("");
  const [employerRole, setEmployerRole] = useState("admin");
  const [employerUid, setEmployerUid] = useState(null);

  // Filters
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [clockedStatus, setClockedStatus] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // 🔢 Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // default: 25 per page

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [entryBeingEdited, setEntryBeingEdited] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");
  const [editReason, setEditReason] = useState("");

  // First-run soft prompt banner
  const [firstRunOpen, setFirstRunOpen] = useState(false);

  // Force clock-out modal
  const [forceModalOpen, setForceModalOpen] = useState(false);
  // Manual add-time modal
  const [manualOpen, setManualOpen] = useState(false);
  const [mUserId, setMUserId] = useState("");
  const [mIn, setMIn] = useState(""); // datetime-local (company TZ)
  const [mOut, setMOut] = useState(""); // datetime-local (company TZ)
  const [mReason, setMReason] = useState("");
  const [mSaving, setMSaving] = useState(false);
  const [mWorkSiteId, setMWorkSiteId] = useState("auto"); // "auto" = use employee default
  //const [mWorkSiteName, setMWorkSiteName] = useState(""); // optional convenience

  const [entryBeingForced, setEntryBeingForced] = useState(null);
  const [forceReason, setForceReason] = useState("");

  // SETTINGS — live values we show as a preview on the page
  const [ttSettings, setTtSettings] = useState({
    enableEarlyClockIn: true,
    earlyClockInMinutes: 15,
    enableLateGrace: true,
    lateGraceMinutes: 5,
    // overtime detection preview
    overtimeBasis: "weekly", // "weekly" = total hours in period, "daily" = per day
    hoursBeforeOvertimePerPeriod: 40,
    dailyOvertimeHours: 8,
  });

  // SETTINGS — modal state + form values
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [formEnableEarly, setFormEnableEarly] = useState(true);
  const [formEarlyMinutes, setFormEarlyMinutes] = useState(15);
  const [formEnableLate, setFormEnableLate] = useState(true);
  const [formLateMinutes, setFormLateMinutes] = useState(5);

  // overtime detection fields in the modal
  const [formOvertimeBasis, setFormOvertimeBasis] = useState("weekly");
  const [formPeriodHours, setFormPeriodHours] = useState(40);
  const [formDailyHours, setFormDailyHours] = useState(8);

  const [savingSettings, setSavingSettings] = useState(false);
  // UI: filter drawer + action menu
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [timeTrackingConfigured, setTimeTrackingConfiguredState] = useState(
    false,
  );

  const navigate = useNavigate();

  const handleOpenTimesheets = () => {
    sessionStorage.setItem("admin.returnTab", "attendance");
    localStorage.setItem("admin.activeTab", "attendance");
    navigate("/employer-timesheets");
  };

  const handleSeedEntries = async () => {
    try {
      const user = auth.currentUser;
      if (!user || !companyId) {
        toast.error("No employer/company available.");
        return;
      }
      const token = await user.getIdToken();
      const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_BASE;

      const resp = await fetch(`${FUNCTIONS_BASE}/seedTestTimeTracking`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId,
          daysBack: 7,
          entriesPerDay: 30,
        }),
      });

      const data = await resp.json();
      if (!resp.ok) {
        console.error(data);
        toast.error(data.error || "Seeding failed");
      } else {
        toast.success(`Seeded ${data.created} time entries.`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error calling seed function.");
    }
  };

  // Init: auth + company + tz
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        auth.onAuthStateChanged(async (user) => {
          if (!user) {
            setError("Employer not authenticated.");
            setLoading(false);
            return;
          }
          setEmployerUid(user.uid);

          const employerRef = doc(db, "users", user.uid);
          const employerDoc = await getDoc(employerRef);
          if (!employerDoc.exists() || !employerDoc.data().companyId) {
            setError("Company ID not found.");
            setLoading(false);
            return;
          }

          const data = employerDoc.data();
          setEmployerName(data?.name || user.email || "manager");
          setEmployerRole(data?.role || "admin");

          // Restore saved time tracking filters (if for this company)
          const savedFilters = data.timeTrackingFilters;
          if (savedFilters && savedFilters.companyId === data.companyId) {
            if (savedFilters.filterEmployee)
              setFilterEmployee(savedFilters.filterEmployee);
            if (savedFilters.filterStatus)
              setFilterStatus(savedFilters.filterStatus);
            if (savedFilters.clockedStatus)
              setClockedStatus(savedFilters.clockedStatus);
            if (savedFilters.startDate) setStartDate(savedFilters.startDate);
            if (savedFilters.endDate) setEndDate(savedFilters.endDate);
            if (savedFilters.pageSize) setPageSize(savedFilters.pageSize);
            // ✅ ADD THIS LINE
            if (savedFilters.filterLocationId)
              setFilterLocationId(savedFilters.filterLocationId);
          }

          const cid = data.companyId;
          setCompanyId(cid);

          const companyRef = doc(db, "companies", cid);
          const companySnap = await getDoc(companyRef);

          const cdata = companySnap.data() || {};
          setCompanyTimeZone(cdata.timeZone || "UTC");

          // ✅ add this
          setCompanyMultiLocationEnabled(Boolean(cdata.multiLocationEnabled));
        });
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    init();
  }, []);

  // Employees (live)
  useEffect(() => {
    if (!companyId) return;
    const employeesRef = collection(db, `companies/${companyId}/employees`);
    const unsub = onSnapshot(
      employeesRef,
      (snap) => {
        const map = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          map[d.id] = {
            name: data.name || "Unknown",
            locationId: data.locationId || null,
            locationName: data.locationName || "",
          };
        });
        setEmployees(map);
      },
      (err) => {
        console.error("Employees live query error:", err);
        setError("Failed to load employees.");
      },
    );
    return () => unsub();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const locSnap = await getDocs(
          collection(db, "companies", companyId, "locations"),
        );
        const locs = locSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // sort: primary first, then name
        locs.sort((a, b) => {
          const ap = a.isPrimary ? -1 : 0;
          const bp = b.isPrimary ? -1 : 0;
          if (ap !== bp) return ap - bp;
          return String(a.name || "").localeCompare(String(b.name || ""));
        });

        setLocations(locs);
      } catch (e) {
        console.error("Failed to load locations:", e);
      }
    })();
  }, [companyId]);

  // Load Time Tracking settings (including overtime detection)
  const fetchTimeTrackingSettings = async (cid) => {
    try {
      const sref = doc(db, `companies/${cid}/settings/timeTracking`);
      const snap = await getDoc(sref);
      if (snap.exists()) {
        const d = snap.data() || {};
        const basis =
          d.overtimeBasis === "daily" || d.overtimeBasis === "weekly"
            ? d.overtimeBasis
            : "weekly";
        const perPeriod =
          typeof d.hoursBeforeOvertimePerPeriod === "number"
            ? d.hoursBeforeOvertimePerPeriod
            : 40;
        const daily =
          typeof d.dailyOvertimeHours === "number" ? d.dailyOvertimeHours : 8;

        setTtSettings({
          enableEarlyClockIn:
            typeof d.enableEarlyClockIn === "boolean"
              ? d.enableEarlyClockIn
              : true,
          earlyClockInMinutes:
            typeof d.earlyClockInMinutes === "number"
              ? d.earlyClockInMinutes
              : 15,
          enableLateGrace:
            typeof d.enableLateGrace === "boolean" ? d.enableLateGrace : true,
          lateGraceMinutes:
            typeof d.lateGraceMinutes === "number" ? d.lateGraceMinutes : 5,
          overtimeBasis: basis,
          hoursBeforeOvertimePerPeriod: perPeriod,
          dailyOvertimeHours: daily,
        });
      } else {
        // Defaults
        setTtSettings({
          enableEarlyClockIn: true,
          earlyClockInMinutes: 15,
          enableLateGrace: true,
          lateGraceMinutes: 5,
          overtimeBasis: "weekly",
          hoursBeforeOvertimePerPeriod: 40,
          dailyOvertimeHours: 8,
        });
      }
    } catch (e) {
      console.warn("Failed to load time tracking settings:", e?.message);
      setTtSettings({
        enableEarlyClockIn: true,
        earlyClockInMinutes: 15,
        enableLateGrace: true,
        lateGraceMinutes: 5,
        overtimeBasis: "weekly",
        hoursBeforeOvertimePerPeriod: 40,
        dailyOvertimeHours: 8,
      });
    }
  };

  useEffect(() => {
    if (companyId) fetchTimeTrackingSettings(companyId);
  }, [companyId]);

  // Time entries (live)
  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    const baseRef = collection(db, `companies/${companyId}/time_tracking`);
    const qRef = query(baseRef, orderBy("date", "desc"), limit(500));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((docSnap) => {
          const d = docSnap.data();
          const loc = d.clockInLocation || {};

          const parts = [];
          if (loc.city) parts.push(loc.city);
          if (loc.region) parts.push(loc.region);
          if (loc.country) parts.push(loc.country);
          const place = parts.join(", ");

          let readableLocation = "—";
          if (place) {
            readableLocation = place;
          } else if (loc.ip) {
            readableLocation = "IP-based location";
          } else if (loc.lat && loc.lng) {
            readableLocation = "Location captured";
          }

          const rawBits = [];
          if (loc.lat && loc.lng) {
            rawBits.push(
              `${Number(loc.lat).toFixed(5)}, ${Number(loc.lng).toFixed(5)}`,
            );
          }
          if (loc.ip) {
            rawBits.push(loc.ip);
          }
          const locationRaw = rawBits.join(" • ");

          const emp = employees[d.userId];

          return {
            id: docSnap.id,
            ...d,
            employeeName: emp?.name || "Unknown",

            // ✅ Work site should come from the time entry (source of truth)
            // Fallback to employee default ONLY for legacy entries missing workSiteId
            workSiteId: d.workSiteId || emp?.locationId || null,
            workSiteName: d.workSiteName || emp?.locationName || "",

            readableLocation,
            locationRaw,
            locationPrecision: loc.precision || "none",
          };
        });

        setTimeEntries(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Time-tracking live query error:", err);
        setError("Failed to load time tracking live data.");
        setLoading(false);
      },
    );

    return () => unsub();
  }, [companyId, employees]);

  const filteredEntries = useMemo(() => {
    return timeEntries.filter((entry) => {
      const matchEmployee =
        filterEmployee === "all" || entry.userId === filterEmployee;

      const matchStatus =
        filterStatus === "all" || entry.status === filterStatus;

      const matchClocked =
        clockedStatus === "all"
          ? true
          : clockedStatus === "clocked-in"
          ? !entry.clockOut
          : !!entry.clockOut;

      const entryDay = entry.clockIn
        ? DateTime.fromISO(entry.clockIn, { zone: companyTimeZone }).toISODate()
        : entry.date;

      const matchStart = startDate ? entryDay >= startDate : true;
      const matchEnd = endDate ? entryDay <= endDate : true;

      const matchSite =
        !companyMultiLocationEnabled ||
        filterLocationId === "all" ||
        entry.workSiteId === filterLocationId;

      return (
        matchEmployee &&
        matchStatus &&
        matchClocked &&
        matchStart &&
        matchEnd &&
        matchSite
      );
    });
  }, [
    timeEntries,
    filterEmployee,
    filterStatus,
    clockedStatus,
    startDate,
    endDate,
    companyTimeZone,
    companyMultiLocationEnabled,
    filterLocationId,
  ]);

  // 🔢 Pagination based on filtered entries
  const totalEntries = filteredEntries.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));

  // When filters change, go back to page 1
  useEffect(() => {
    setPage(1);
  }, [
    filterEmployee,
    filterStatus,
    clockedStatus,
    startDate,
    endDate,
    pageSize,
    filterLocationId, // ✅ add
  ]);

  // Make sure current page is not out of range
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const startIndex = (page - 1) * pageSize;

  const pagedEntries = useMemo(
    () => filteredEntries.slice(startIndex, startIndex + pageSize),
    [filteredEntries, startIndex, pageSize],
  );

  const periodLabel = useMemo(() => {
    if (startDate && endDate) {
      return `${startDate} – ${endDate}`;
    }
    if (startDate) {
      return `from ${startDate}`;
    }
    if (endDate) {
      return `up to ${endDate}`;
    }
    return "all dates";
  }, [startDate, endDate]);

  // 🔢 NEW: overtime summary based on current filters + TT settings
  const overtimeSummary = useMemo(() => {
    if (!filteredEntries.length) {
      return { total: 0, regular: 0, overtime: 0 };
    }

    // Only completed sessions with numeric hoursWorked
    const completed = filteredEntries.filter((e) => {
      const hrs = Number(e.hoursWorked);
      // Any completed entry with a positive number of hours counts,
      // including paid leave.
      if (!e.clockOut || !Number.isFinite(hrs) || hrs <= 0) return false;
      return true;
    });

    if (!completed.length) {
      return { total: 0, regular: 0, overtime: 0 };
    }

    // DAILY BASIS – sum per day, then split
    if (ttSettings.overtimeBasis === "daily") {
      const byDay = {};
      completed.forEach((e) => {
        const day =
          e.date ||
          (e.clockIn
            ? DateTime.fromISO(e.clockIn, { zone: companyTimeZone }).toISODate()
            : null);
        if (!day) return;
        const hrs = Number(e.hoursWorked) || 0;
        byDay[day] = (byDay[day] || 0) + hrs;
      });

      let total = 0;
      let regular = 0;
      let overtime = 0;
      const limit = ttSettings.dailyOvertimeHours || 0;

      Object.values(byDay).forEach((hrs) => {
        const h = Number(hrs) || 0;
        total += h;
        if (limit > 0) {
          const reg = Math.min(h, limit);
          const ot = Math.max(h - limit, 0);
          regular += reg;
          overtime += ot;
        } else {
          regular += h;
        }
      });

      return {
        total: Number(total.toFixed(2)),
        regular: Number(regular.toFixed(2)),
        overtime: Number(overtime.toFixed(2)),
      };
    }

    // PERIOD BASIS – treat the current filtered range as one "period"
    const total = completed.reduce(
      (sum, e) => sum + (Number(e.hoursWorked) || 0),
      0,
    );
    const threshold = ttSettings.hoursBeforeOvertimePerPeriod || 0;

    let regular = total;
    let overtime = 0;
    if (threshold > 0) {
      regular = Math.min(total, threshold);
      overtime = Math.max(total - threshold, 0);
    }

    return {
      total: Number(total.toFixed(2)),
      regular: Number(regular.toFixed(2)),
      overtime: Number(overtime.toFixed(2)),
    };
  }, [
    filteredEntries,
    ttSettings.overtimeBasis,
    ttSettings.hoursBeforeOvertimePerPeriod,
    ttSettings.dailyOvertimeHours,
    companyTimeZone,
  ]);

  const handleApplyFilters = async () => {
    setFiltersOpen(false);

    const user = auth.currentUser;
    if (!user || !companyId) return;

    const payload = {
      companyId,
      filterEmployee,
      filterStatus,
      clockedStatus,
      startDate,
      endDate,
      pageSize,
      // ✅ add
      filterLocationId,
    };

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, { timeTrackingFilters: payload }, { merge: true });
    } catch (e) {
      console.error("Failed to save time tracking filters", e);
    }
  };

  const handleResetFilters = async () => {
    setFilterEmployee("all");
    setFilterStatus("all");
    setClockedStatus("all");
    setStartDate("");
    setEndDate("");
    setPageSize(25);
    setPage(1);
    setFiltersOpen(false);
    setFilterLocationId("all");
    const user = auth.currentUser;
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        { timeTrackingFilters: deleteField() },
        { merge: true },
      );
    } catch (e) {
      console.error("Failed to clear time tracking filters", e);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "—";
    return DateTime.fromISO(timestamp, { zone: companyTimeZone }).toFormat(
      "hh:mm a",
    );
  };

  const formatDateMDY = (dateISO) => {
    if (!dateISO) return "—";

    // entry.date is typically "YYYY-MM-DD"
    const d = DateTime.fromISO(dateISO, { zone: companyTimeZone });
    if (d.isValid) return d.toFormat("MM/dd/yyyy");

    // fallback (in case it ever comes in as full ISO timestamp)
    const d2 = DateTime.fromISO(String(dateISO), { zone: companyTimeZone });
    return d2.isValid ? d2.toFormat("MM/dd/yyyy") : "—";
  };

  // ===== Edit existing entry =====
  const openEditModal = async (entry) => {
    if (await isEntryLocked(companyId, entry, companyTimeZone)) {
      toast.error(
        "This week is approved/locked. Unlock the week in Timesheets to edit.",
      );
      return;
    }
    setEntryBeingEdited(entry);
    setEditClockIn(entry.clockIn || "");
    setEditClockOut(entry.clockOut || "");
    setEditReason("");
    setEditModalOpen(true);
  };

  const handleTimeUpdate = async () => {
    if (!entryBeingEdited || !companyId) return;

    try {
      const ref = doc(
        db,
        `companies/${companyId}/time_tracking`,
        entryBeingEdited.id,
      );

      const updatedData = {
        clockIn: editClockIn,
        clockOut: editClockOut,
        wasEdited: true,
        editReason: editReason.trim(),
      };

      const clockInTime = DateTime.fromISO(editClockIn, {
        zone: companyTimeZone,
      });
      let clockOutTime = DateTime.fromISO(editClockOut, {
        zone: companyTimeZone,
      });

      if (clockInTime.isValid && clockOutTime.isValid) {
        clockOutTime = normalizeOutAfterIn(clockInTime, clockOutTime);
        const hours = clockOutTime.diff(clockInTime, "hours").hours;
        updatedData.hoursWorked = hours > 0 ? parseFloat(hours.toFixed(2)) : 0;

        // Optional: store normalized clockOut so DB is consistent
        updatedData.clockOut = clockOutTime.toISO();
      }

      await updateDoc(ref, updatedData);
      toast.success("Time updated successfully.");

      const updatedEntries = timeEntries.map((entry) =>
        entry.id === entryBeingEdited.id ? { ...entry, ...updatedData } : entry,
      );
      setTimeEntries(updatedEntries);

      setEditModalOpen(false);
    } catch (err) {
      toast.error("Failed to update time.");
      console.error(err);
    }
  };

  // ===== Force Clock-Out =====
  const openForceModal = async (entry) => {
    if (entry.clockOut) return;
    if (await isEntryLocked(companyId, entry, companyTimeZone)) {
      toast.error(
        "This week is approved/locked. Unlock the week in Timesheets to force clock-out.",
      );
      return;
    }
    setEntryBeingForced(entry);
    setForceReason("");
    setForceModalOpen(true);
  };

  const handleForceClockOut = async () => {
    if (!entryBeingForced || !companyId) return;
    if (!forceReason.trim() || forceReason.trim().length < 4) {
      toast.error("Please provide a brief reason (min 4 characters).");
      return;
    }

    try {
      const ref = doc(
        db,
        `companies/${companyId}/time_tracking`,
        entryBeingForced.id,
      );

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        toast.error("Time entry not found.");
        setForceModalOpen(false);
        return;
      }
      const data = snap.data();
      if (data.clockOut) {
        toast.info(
          `Already clocked out at ${formatTime(
            data.clockOut,
          )}. No action taken.`,
        );
        setForceModalOpen(false);
        return;
      }

      const nowTz = DateTime.now().setZone(companyTimeZone);
      const clockInTz = DateTime.fromISO(data.clockIn, {
        zone: companyTimeZone,
      });
      const hours = Math.max(
        0,
        parseFloat(nowTz.diff(clockInTz, "hours").hours.toFixed(2)),
      );

      const updated = {
        clockOut: nowTz.toISO(),
        hoursWorked: hours,
        forcedClockOut: true,
        forcedBy: {
          uid: employerUid,
          name: employerName || "manager",
          role: employerRole || "admin",
        },
        forcedReason: forceReason.trim(),
        forcedAt: serverTimestamp(),
      };

      await updateDoc(ref, updated);

      const newEntries = timeEntries.map((e) =>
        e.id === entryBeingForced.id ? { ...e, ...updated } : e,
      );
      setTimeEntries(newEntries);

      toast.success("Forced clock-out recorded.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to force clock-out.");
    } finally {
      setForceModalOpen(false);
    }
  };

  // ===== Manual Add Time =====
  const openManualModal = async () => {
    setMUserId("");
    setMIn("");
    setMOut("");
    setMReason("");

    // ✅ ADD THIS: default to primary location (or first active), only if multi-location is enabled
    if (companyMultiLocationEnabled) {
      const activeLocs = locations.filter((l) => l.active !== false);
      const primary = activeLocs.find((l) => l.isPrimary) || activeLocs[0];
      setMWorkSiteId(primary?.id || "");
    } else {
      setMWorkSiteId("");
    }
    setManualOpen(true);
  };

  // overlap check helper (simple interval test)
  function intervalsOverlap(aStart, aEnd, bStart, bEnd) {
    return (
      aStart.toMillis() < bEnd.toMillis() && bStart.toMillis() < aEnd.toMillis()
    );
  }

  function normalizeOutAfterIn(inTz, outTz) {
    if (!inTz?.isValid || !outTz?.isValid) return outTz;

    // If out is <= in, assume shift crossed midnight → add 1 day
    return outTz.toMillis() <= inTz.toMillis()
      ? outTz.plus({ days: 1 })
      : outTz;
  }

  const handleCreateManual = async () => {
    if (!companyId) return;
    if (!mUserId || !mIn || !mOut || !mReason.trim()) {
      toast.error("Select employee, set both times, and add a reason.");
      return;
    }

    if (companyMultiLocationEnabled && !mWorkSiteId) {
      toast.error("Select a work site.");
      return;
    }

    try {
      setMSaving(true);
      const tz = companyTimeZone;

      const inTz = DateTime.fromISO(mIn, { zone: tz });
      let outTz = DateTime.fromISO(mOut, { zone: tz });

      if (!inTz.isValid || !outTz.isValid) {
        toast.error("Check your times.");
        setMSaving(false);
        return;
      }

      outTz = normalizeOutAfterIn(inTz, outTz);

      if (outTz.toMillis() <= inTz.toMillis()) {
        toast.error("Clock-out must be after clock-in.");
        setMSaving(false);
        return;
      }

      const dateISO = inTz.toISODate();

      if (await isEntryLocked(companyId, { date: dateISO }, tz)) {
        toast.error(
          "This week is approved/locked. Unlock the week to add time.",
        );
        setMSaving(false);
        return;
      }

      const baseRef = collection(db, `companies/${companyId}/time_tracking`);
      const prevISO = inTz.minus({ days: 1 }).toISODate();
      const nextISO = inTz.plus({ days: 1 }).toISODate();

      const qRef = query(
        baseRef,
        where("userId", "==", mUserId),
        where("date", ">=", prevISO),
        where("date", "<=", nextISO),
      );
      const snap = await getDocs(qRef);

      const conflict = snap.docs.some((d) => {
        const e = d.data();
        if (!e.clockIn || !e.clockOut) return false;
        const eIn = DateTime.fromISO(e.clockIn, { zone: tz });
        let eOut = DateTime.fromISO(e.clockOut, { zone: tz });
        eOut = normalizeOutAfterIn(eIn, eOut);
        return intervalsOverlap(inTz, outTz, eIn, eOut);
      });

      if (conflict) {
        toast.error("Overlaps another entry for this employee. Adjust times.");
        setMSaving(false);
        return;
      }

      const hours = parseFloat(outTz.diff(inTz, "hours").hours.toFixed(2));

      // ✅ Use the selected company location as the work site
      const selectedLoc = companyMultiLocationEnabled
        ? locations.find((l) => l.id === mWorkSiteId)
        : null;

      const workSiteIdToSave = companyMultiLocationEnabled
        ? selectedLoc?.id || null
        : null;

      const workSiteNameToSave = companyMultiLocationEnabled
        ? selectedLoc?.name || ""
        : "";

      const newRef = doc(baseRef);
      await setDoc(newRef, {
        id: newRef.id,
        userId: mUserId,
        employeeName: employees[mUserId]?.name || "Unknown",
        clockIn: inTz.toISO(),
        clockOut: outTz.toISO(),
        hoursWorked: hours,
        workSiteId: workSiteIdToSave,
        workSiteName: workSiteNameToSave,

        date: dateISO,
        status: "On Time",
        manualEntry: true,
        manualReason: mReason.trim(),
        createdAt: serverTimestamp(),
        createdBy: {
          uid: employerUid,
          name: employerName || "manager",
          role: employerRole || "admin",
        },
        needsAck: true,
      });

      toast.success("Manual time entry created.");
      setManualOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to create manual entry.");
    } finally {
      setMSaving(false);
    }
  };

  // ===== SETTINGS helpers =====
  const clampNum = (val, min, max, fallback) => {
    const n = Number(val);
    if (Number.isFinite(n)) return Math.min(Math.max(n, min), max);
    return fallback;
  };

  const openSettingsModal = useCallback(() => {
    setFormEnableEarly(ttSettings.enableEarlyClockIn);
    setFormEarlyMinutes(ttSettings.earlyClockInMinutes);
    setFormEnableLate(ttSettings.enableLateGrace);
    setFormLateMinutes(ttSettings.lateGraceMinutes);

    setFormOvertimeBasis(ttSettings.overtimeBasis || "weekly");
    setFormPeriodHours(
      typeof ttSettings.hoursBeforeOvertimePerPeriod === "number"
        ? ttSettings.hoursBeforeOvertimePerPeriod
        : 40,
    );
    setFormDailyHours(
      typeof ttSettings.dailyOvertimeHours === "number"
        ? ttSettings.dailyOvertimeHours
        : 8,
    );

    setSettingsOpen(true);
  }, [
    ttSettings.enableEarlyClockIn,
    ttSettings.earlyClockInMinutes,
    ttSettings.enableLateGrace,
    ttSettings.lateGraceMinutes,
    ttSettings.overtimeBasis,
    ttSettings.hoursBeforeOvertimePerPeriod,
    ttSettings.dailyOvertimeHours,
  ]);

  useEffect(() => {
    if (!companyId) return;

    const ref = doc(db, "companies", companyId, "firstRun", "flags");

    return onSnapshot(ref, (snap) => {
      setTimeTrackingConfiguredState(!!snap.data()?.timeTrackingConfigured);
    });
  }, [companyId]);

  // First-run: soft prompt + auto-open (but allow cancel)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const configured = await getTimeTrackingConfigured(db, companyId);
        if (!configured) {
          setFirstRunOpen(true);
          openSettingsModal();
        }
      } catch (e) {
        console.warn("First-run check failed:", e);
      }
    })();
  }, [companyId, openSettingsModal]);

  const saveSettings = async () => {
    if (!companyId) return;
    try {
      setSavingSettings(true);
      const early = clampNum(formEarlyMinutes, 0, 180, 15);
      const grace = clampNum(formLateMinutes, 0, 120, 5);
      const periodHours = clampNum(formPeriodHours, 0, 1000, 40);
      const dailyHours = clampNum(formDailyHours, 0, 24, 8);

      const payload = {
        enableEarlyClockIn: formEnableEarly,
        earlyClockInMinutes: early,
        enableLateGrace: formEnableLate,
        lateGraceMinutes: grace,
        overtimeBasis: formOvertimeBasis,
        hoursBeforeOvertimePerPeriod: periodHours,
        dailyOvertimeHours: dailyHours,
        updatedAt: serverTimestamp(),
        updatedBy: { uid: employerUid, name: employerName || "manager" },
      };

      await setDoc(
        doc(db, `companies/${companyId}/settings/timeTracking`),
        payload,
        { merge: true },
      );
      await setDoc(
        doc(db, "companies", companyId),
        {
          quickSetupProgress: {
            configuredTimeTracking: true,
          },
        },
        { merge: true },
      );
      setTtSettings({
        enableEarlyClockIn: formEnableEarly,
        earlyClockInMinutes: early,
        enableLateGrace: formEnableLate,
        lateGraceMinutes: grace,
        overtimeBasis: formOvertimeBasis,
        hoursBeforeOvertimePerPeriod: periodHours,
        dailyOvertimeHours: dailyHours,
      });

      if (firstRunOpen) {
        try {
          await setTimeTrackingConfigured(db, companyId);
        } catch (_) {}
        setFirstRunOpen(false);
      }

      toast.success("Time Tracking settings saved.");
      setSettingsOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setSettingsOpen(false);
    if (settingsOpen) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [settingsOpen]);
  const hasAnyEntries = timeEntries.length > 0;

  // ──────────────────────────────────────────────────────────────────────────────
  return (
    <div className="page-title-container">
      <div className="time-tracking-wrapper">
        {/* Header: Filter + Actions */}
        <header className="tt-header-row">
          <div className="tt-actions-wrapper">
            <button
              type="button"
              className="tt-action-trigger"
              onClick={() => setActionsOpen((open) => !open)}
            >
              Action
              <span className="tt-action-caret" aria-hidden="true">
                ▾
              </span>
            </button>

            {actionsOpen && (
              <div className="tt-action-menu">
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(false);
                    openSettingsModal();
                  }}
                >
                  Settings
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(false);
                    handleOpenTimesheets();
                  }}
                >
                  Timesheets
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(false);
                    openManualModal();
                  }}
                >
                  Add Time
                </button>
                {false && (
                  <button
                    type="button"
                    className="tt-action-dev"
                    onClick={() => {
                      setActionsOpen(false);
                      handleSeedEntries();
                    }}
                  >
                    Seed Time Entries
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="tt-header-right">
            <button
              type="button"
              className={`tt-filter-toggle ${filtersOpen ? "is-active" : ""}`}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <span className="tt-filter-icon" aria-hidden="true">
                <SlidersHorizontal size={16} />
              </span>
              <span>Filter</span>
            </button>
          </div>
        </header>

        {/* Soft first-run callout */}
        {firstRunOpen && (
          <div
            className="banner-callout"
            role="status"
            aria-live="polite"
            style={{
              margin: "12px 0",
              padding: "10px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span aria-hidden>⚙️</span>
              <div>
                <strong>Complete Time Tracking setup</strong>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Choose early clock-in, late grace, and overtime rules. You can
                  do it later too.
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="settingsbtn solid"
                type="button"
                onClick={openSettingsModal}
                title="Open Time Tracking Settings"
              >
                Open settings
              </button>
              <button
                className="timesheetbtn ghost"
                type="button"
                onClick={() => setFirstRunOpen(false)}
                title="Hide reminder for now"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* NEW: Overtime summary bar */}
        {filteredEntries.length > 0 && (
          <div
            className="ot-summary-bar"
            aria-label="Overtime summary for current view"
          >
            <div className="ot-summary-left">
              <div className="ot-summary-title">Hours overview</div>
              <div className="ot-summary-sub">
                For {periodLabel} · completed sessions only.
              </div>
            </div>
            <div className="ot-summary-metrics">
              <div className="ot-chip ot-chip-total">
                <span className="ot-chip-label">Total</span>
                <span className="ot-chip-value">
                  {overtimeSummary.total.toFixed(2)} hrs
                </span>
              </div>
              <div className="ot-chip ot-chip-regular">
                <span className="ot-chip-label">Regular</span>
                <span className="ot-chip-value">
                  {overtimeSummary.regular.toFixed(2)} hrs
                </span>
              </div>
              <div className="ot-chip ot-chip-overtime">
                <span className="ot-chip-label">Overtime</span>
                <span className="ot-chip-value">
                  {overtimeSummary.overtime.toFixed(2)} hrs
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Main area: table */}
        <div className="tt-main">
          <div className="tt-main-table">
            {loading ? (
              <p className="loading-text">Loading time tracking data...</p>
            ) : error ? (
              <p className="error-text">{error}</p>
            ) : filteredEntries.length === 0 ? (
              !hasAnyEntries ? (
                // ✅ TRUE EMPTY STATE (no time entries exist at all)
                <div className="tt-empty">
                  <img
                    src={NoDataIllustration}
                    alt="No time entries yet"
                    className="tt-empty__img"
                  />

                  <h3 className="tt-empty__title">No time entries yet</h3>

                  <p className="tt-empty__text">
                    Once employees clock in, you’ll see attendance, hours
                    worked, and overtime here.
                  </p>

                  <div className="leave-empty__steps">
                    <div
                      className={`leave-empty__step ${
                        timeTrackingConfigured ? "done" : ""
                      }`}
                    >
                      <span
                        className={`leave-empty__chip ${
                          timeTrackingConfigured ? "done" : ""
                        }`}
                      >
                        {timeTrackingConfigured ? "✓" : "1"}
                      </span>
                      {timeTrackingConfigured
                        ? "Time tracking settings configured"
                        : "Configure time tracking settings"}
                    </div>
                    <div className="leave-empty__step">
                      <span className="leave-empty__chip">2</span>
                      Wait for employees to clock in
                    </div>
                    <div className="leave-empty__step">
                      <span className="leave-empty__chip">3</span>
                      Start reviewing time
                    </div>
                  </div>
                  <div className="ex-empty__hint">
                    Tip: Configure your early clock-in, late grace and overtime
                    detection in the settings first so employees can utilize the
                    clock-in feature.
                  </div>
                </div>
              ) : (
                // ✅ FILTERED EMPTY (there is data, but filters return nothing)
                <div className="tt-noresults">
                  <div className="tt-noresults__title">
                    No matching records found
                  </div>
                  <div className="tt-noresults__text">
                    Try adjusting your filters, date range, or work site.
                  </div>
                </div>
              )
            ) : (
              <div className="table-card">
                <div className="table-scroll">
                  <table className="styled-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>Clock-In</th>
                        <th>Clock-Out</th>
                        <th>Hours Worked</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Work Site</th>

                        <th>Edited?</th>
                        <th>Edit Reason</th>
                        <th>Forced?</th>
                        <th>Force Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedEntries.map((entry) => {
                        const isLeaveEntry =
                          entry.entryType === "paid-leave" ||
                          entry.status === "On Leave";
                        const leaveLabel =
                          entry.leaveTypeLabel ||
                          entry.leaveType ||
                          "Paid leave";

                        return (
                          <tr key={entry.id}>
                            <td className="actions-cellss">
                              <button
                                className="timeedit-button"
                                onClick={() => openEditModal(entry)}
                              >
                                Edit
                              </button>
                              {!entry.clockOut && (
                                <button
                                  className="force-button"
                                  onClick={() => openForceModal(entry)}
                                  title="Force clock-out now (requires reason)"
                                >
                                  Force Clock-Out
                                </button>
                              )}
                            </td>

                            <td>{entry.employeeName}</td>
                            <td>{formatDateMDY(entry.date)}</td>

                            {/* Clock-In */}
                            <td>
                              {isLeaveEntry ? (
                                <div className="leave-clock-cell">
                                  <span className="leave-clock-main">
                                    Paid leave
                                  </span>
                                  <span className="leave-clock-sub">
                                    {leaveLabel}
                                  </span>
                                </div>
                              ) : (
                                formatTime(entry.clockIn)
                              )}
                            </td>

                            {/* Clock-Out */}
                            <td>
                              {isLeaveEntry ? (
                                <div className="leave-clock-cell">
                                  <span className="leave-clock-main">
                                    Paid leave
                                  </span>
                                  <span className="leave-clock-sub">
                                    {leaveLabel}
                                  </span>
                                </div>
                              ) : (
                                formatTime(entry.clockOut)
                              )}
                            </td>

                            <td>
                              {entry.hoursWorked
                                ? `${Number(entry.hoursWorked).toFixed(2)} hrs`
                                : "-"}
                            </td>

                            <td title={entry.locationRaw || undefined}>
                              {entry.readableLocation}
                              {entry.locationPrecision === "gps" && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 11,
                                    color: "#16a34a",
                                  }}
                                >
                                  GPS
                                </span>
                              )}
                              {entry.locationPrecision === "ip-only" && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 11,
                                    color: "#f97316",
                                  }}
                                >
                                  IP
                                </span>
                              )}
                            </td>

                            <td>
                              {entry.entryType === "paid-leave" ||
                              entry.status === "On Leave" ? (
                                <div className="status-with-tag">
                                  <span className="status-pill on-leave">
                                    On Leave
                                  </span>
                                  <span className="leave-type-chip">
                                    {entry.leaveTypeLabel ||
                                      entry.leaveType ||
                                      "Paid leave"}
                                  </span>
                                </div>
                              ) : (
                                <span
                                  className={`status-pill ${entry.status
                                    ?.toLowerCase()
                                    .replace(/\s/g, "-")}`}
                                >
                                  {entry.status}
                                </span>
                              )}
                            </td>
                            <td>
                              {companyMultiLocationEnabled
                                ? entry.workSiteName || "—"
                                : "—"}
                            </td>

                            <td>
                              {entry.wasEdited ? (
                                <span
                                  style={{ color: "#d97706", fontWeight: 600 }}
                                >
                                  Yes
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {entry.wasEdited && entry.editReason ? (
                                <span title={entry.editReason}>
                                  {entry.editReason}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>

                            <td>
                              {entry.forcedClockOut ? (
                                <span className="forced-pill">Forced</span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {entry.forcedClockOut && entry.forcedReason ? (
                                <span title={entry.forcedReason}>
                                  {entry.forcedReason}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalEntries > pageSize && clockedStatus !== "clocked-in" && (
                  <div className="pagination-bar">
                    <div className="pagination-info">
                      Showing {startIndex + 1}–
                      {Math.min(startIndex + pageSize, totalEntries)} of{" "}
                      {totalEntries}
                    </div>

                    <div className="pagination-controls">
                      <button
                        type="button"
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                      >
                        ⟪ First
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        ‹ Prev
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={page === totalPages}
                      >
                        Next ›
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                      >
                        Last ⟫
                      </button>

                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value) || 25);
                          setPage(1);
                        }}
                      >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Edit Modal */}
                {editModalOpen && (
                  <div className="modal-overlay">
                    <div className="modal-content">
                      <h2>Edit Time Entry</h2>

                      <p>
                        <strong>Employee:</strong>{" "}
                        {entryBeingEdited?.employeeName}
                      </p>
                      <p>
                        <strong>Date:</strong>{" "}
                        {formatDateMDY(entryBeingEdited?.date)}
                      </p>

                      <label>
                        Clock-In:
                        <input
                          type="datetime-local"
                          value={editClockIn}
                          onChange={(e) => setEditClockIn(e.target.value)}
                        />
                      </label>

                      <label>
                        Clock-Out:
                        <input
                          type="datetime-local"
                          value={editClockOut}
                          onChange={(e) => setEditClockOut(e.target.value)}
                        />
                      </label>

                      <label>
                        Reason for Edit:
                        <textarea
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          rows={3}
                          placeholder="Optional explanation (e.g., forgot to clock out)"
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            marginTop: "0.5rem",
                          }}
                        />
                      </label>

                      <div className="modal-buttons">
                        <button
                          className="general-savebuttons"
                          onClick={handleTimeUpdate}
                        >
                          Save Changes
                        </button>
                        <button
                          className="general-cancelbuttons"
                          onClick={() => setEditModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Force Clock-Out Modal */}
                {forceModalOpen && (
                  <div className="modal-overlay">
                    <div className="modal-content">
                      <h2>Force Clock-Out</h2>

                      <p style={{ marginBottom: 0 }}>
                        <strong>Employee:</strong>{" "}
                        {entryBeingForced?.employeeName || "—"}
                      </p>
                      <p style={{ marginTop: 4 }}>
                        <strong>Clock-in:</strong>{" "}
                        {formatTime(entryBeingForced?.clockIn)}
                      </p>

                      <p>
                        <strong>Clocking out as:</strong>{" "}
                        {DateTime.now()
                          .setZone(companyTimeZone)
                          .toFormat("DDD t")}{" "}
                        ({companyTimeZone})
                      </p>

                      <label>
                        Reason (required):
                        <textarea
                          value={forceReason}
                          onChange={(e) => setForceReason(e.target.value)}
                          rows={3}
                          placeholder="e.g., employee forgot to clock out after shift"
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            marginTop: "0.5rem",
                          }}
                        />
                      </label>

                      <div className="modal-buttons">
                        <button
                          className="general-savebuttons"
                          onClick={handleForceClockOut}
                        >
                          Confirm
                        </button>
                        <button
                          className="general-cancelbuttons"
                          onClick={() => setForceModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* FILTER OVERLAY */}
        {filtersOpen &&
          createPortal(
            <div
              className="tt-filter-overlay"
              onClick={() => setFiltersOpen(false)}
              aria-modal="true"
              role="dialog"
            >
              <div
                className="tt-filter-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="tt-filter-header">
                  <div className="tt-filter-header-left">
                    <span className="tt-filter-header-icon">
                      <SlidersHorizontal size={18} />
                    </span>
                  </div>
                  <button
                    type="button"
                    className="tt-filter-close"
                    onClick={() => setFiltersOpen(false)}
                    aria-label="Close filters"
                  >
                    ✕
                  </button>
                </header>

                <div className="tt-filter-body">
                  <div className="filters-grid">
                    <div className="field">
                      <label className="label" htmlFor="flt-employee">
                        Employee
                      </label>
                      <select
                        id="flt-employee"
                        className="control"
                        value={filterEmployee}
                        onChange={(e) => setFilterEmployee(e.target.value)}
                      >
                        <option value="all">All Employees</option>
                        {Object.entries(employees).map(([id, emp]) => (
                          <option key={id} value={id}>
                            {emp?.name || "Unknown"}
                          </option>
                        ))}
                      </select>
                    </div>
                    {companyMultiLocationEnabled && (
                      <div className="field">
                        <label className="label" htmlFor="flt-site">
                          Work Site
                        </label>
                        <select
                          id="flt-site"
                          className="control"
                          value={filterLocationId}
                          onChange={(e) => setFilterLocationId(e.target.value)}
                        >
                          <option value="all">All Sites</option>
                          {locations
                            .filter((l) => l.active !== false)
                            .map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.name || "Unnamed Location"}
                                {loc.isPrimary ? " (Primary)" : ""}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                    <div className="field">
                      <label className="label" htmlFor="flt-status">
                        Status
                      </label>
                      <select
                        id="flt-status"
                        className="control"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        <option value="On Time">On Time</option>
                        <option value="Late Clock-in">Late Clock-in</option>
                        <option value="Early Clock-in">Early Clock-in</option>
                        <option value="On Leave">On Leave</option>
                      </select>
                    </div>

                    <div className="field">
                      <label className="label" htmlFor="flt-clocked">
                        Clocked Session
                      </label>
                      <select
                        id="flt-clocked"
                        className="control"
                        value={clockedStatus}
                        onChange={(e) => setClockedStatus(e.target.value)}
                      >
                        <option value="all">All Sessions</option>
                        <option value="clocked-in">Currently Clocked In</option>
                        <option value="clocked-out">Completed Only</option>
                      </select>
                    </div>

                    <div className="field">
                      <label className="label" htmlFor="flt-start">
                        Start Date
                      </label>
                      <input
                        id="flt-start"
                        className="control"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>

                    <div className="field">
                      <label className="label" htmlFor="flt-end">
                        End Date
                      </label>
                      <input
                        id="flt-end"
                        className="control"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="tt-filter-footer">
                  <button
                    type="button"
                    className="tt-filter-reset"
                    onClick={handleResetFilters}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="tt-filter-apply"
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Manual Add-Time Modal */}
        {manualOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Add Manual Time</h2>

              <label style={{ display: "block", marginBottom: 10 }}>
                Employee:
                <select
                  value={mUserId}
                  onChange={(e) => setMUserId(e.target.value)}
                  className="control"
                  style={{ display: "block", width: "100%", marginTop: 6 }}
                >
                  <option value="">Select employee</option>
                  {Object.entries(employees).map(([uid, emp]) => (
                    <option key={uid} value={uid}>
                      {emp?.name || "Unknown"}
                    </option>
                  ))}
                </select>
              </label>

              {companyMultiLocationEnabled && (
                <label style={{ display: "block", marginBottom: 10 }}>
                  Work Site:
                  <select
                    value={mWorkSiteId}
                    onChange={(e) => setMWorkSiteId(e.target.value)}
                    className="control"
                    style={{ display: "block", width: "100%", marginTop: 6 }}
                  >
                    <option value="">Select work site</option>
                    {locations
                      .filter((l) => l.active !== false)
                      .map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name || "Unnamed Location"}
                          {loc.isPrimary ? " (Primary)" : ""}
                        </option>
                      ))}
                  </select>
                </label>
              )}

              <label style={{ display: "block", marginBottom: 10 }}>
                Clock-In:
                <input
                  type="datetime-local"
                  value={mIn}
                  onChange={(e) => setMIn(e.target.value)}
                  className="control"
                  style={{ display: "block", width: "100%", marginTop: 6 }}
                />
              </label>

              <label style={{ display: "block", marginBottom: 10 }}>
                Clock-Out:
                <input
                  type="datetime-local"
                  value={mOut}
                  onChange={(e) => setMOut(e.target.value)}
                  className="control"
                  style={{ display: "block", width: "100%", marginTop: 6 }}
                />
              </label>

              <label style={{ display: "block", marginBottom: 10 }}>
                Reason (required):
                <textarea
                  rows={3}
                  value={mReason}
                  onChange={(e) => setMReason(e.target.value)}
                  placeholder="Employee forgot to clock in…"
                  className="control"
                  style={{ display: "block", width: "100%", marginTop: 6 }}
                />
              </label>

              <div className="modal-buttons">
                <button
                  className="general-savebuttons"
                  onClick={handleCreateManual}
                  disabled={mSaving}
                >
                  {mSaving ? "Saving…" : "Create Entry"}
                </button>
                <button
                  className="general-cancelbuttons"
                  onClick={() => setManualOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SETTINGS Modal portal */}
        {settingsOpen &&
          createPortal(
            <div
              className="modal-overlay blur"
              onClick={() => setSettingsOpen(false)}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="modal-content modal-lg"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="timemodal-header">
                  <div className="timemodal-titlewrap">
                    <div className="timemodal-icon">
                      <button
                        className="timemodalicon-btn"
                        onClick={() => setSettingsOpen(false)}
                        aria-label="Close"
                      >
                        ✕
                      </button>
                    </div>
                    <div>
                      <h2 className="timemodal-title">
                        Time Tracking Settings
                      </h2>
                      <p className="timemodal-subtitle">
                        These rules apply when employees clock in on their
                        phones or the web. Overtime rules here are also used by
                        Payroll to split regular vs overtime hours.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview chips */}
                <div className="policy-preview tight">
                  <span className="chip">
                    Early: {formEnableEarly ? "ON" : "OFF"}
                    {formEnableEarly ? ` (${formEarlyMinutes}m)` : ""}
                  </span>
                  <span className="chip">
                    Late Grace: {formEnableLate ? "ON" : "OFF"}
                    {formEnableLate ? ` (${formLateMinutes}m)` : ""}
                  </span>
                  <span className="chip">
                    OT:{" "}
                    {formOvertimeBasis === "daily"
                      ? `Daily > ${formDailyHours}h`
                      : `Period > ${formPeriodHours}h`}
                  </span>
                </div>

                {/* Body grid */}
                <div className="settings-grid">
                  {/* EARLY CLOCK-IN */}
                  <section className="settings-card">
                    <header className="settings-card-head">
                      <div>
                        <h3>Early Clock-In</h3>
                        <p className="muted">
                          Allow employees to clock in a bit before the shift
                          starts.
                        </p>
                      </div>
                      <label className="ui-switch">
                        <input
                          type="checkbox"
                          checked={formEnableEarly}
                          onChange={(e) => setFormEnableEarly(e.target.checked)}
                        />
                        <span className="earlyslider" />
                      </label>
                    </header>

                    <div className="settings-row compact">
                      <label className="field-label">
                        Minutes allowed before start
                      </label>
                      <div className="spin">
                        <button
                          type="button"
                          onClick={() =>
                            setFormEarlyMinutes((v) => Math.max(0, v - 1))
                          }
                          disabled={!formEnableEarly}
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={0}
                          max={180}
                          step={1}
                          value={formEarlyMinutes}
                          onChange={(e) =>
                            setFormEarlyMinutes(Number(e.target.value) || 0)
                          }
                          disabled={!formEnableEarly}
                          aria-label="Early minutes"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setFormEarlyMinutes((v) => Math.min(180, v + 1))
                          }
                          disabled={!formEnableEarly}
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <p className="help">
                      <strong>0</strong> = no early clock-in allowed. Range:
                      0–180 minutes.
                    </p>
                  </section>

                  {/* LATE GRACE */}
                  <section className="settings-card">
                    <header className="settings-card-head">
                      <div>
                        <h3>Late Grace</h3>
                        <p className="muted">
                          Treat clock-ins within this window after start as
                          on-time.
                        </p>
                      </div>
                      <label className="ui-switch">
                        <input
                          type="checkbox"
                          checked={formEnableLate}
                          onChange={(e) => setFormEnableLate(e.target.checked)}
                        />
                        <span className="earlyslider" />
                      </label>
                    </header>

                    <div className="settings-row compact">
                      <label className="field-label">
                        Minutes tolerated after start
                      </label>
                      <div className="spin">
                        <button
                          type="button"
                          onClick={() =>
                            setFormLateMinutes((v) => Math.max(0, v - 1))
                          }
                          disabled={!formEnableLate}
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={0}
                          max={120}
                          step={1}
                          value={formLateMinutes}
                          onChange={(e) =>
                            setFormLateMinutes(Number(e.target.value) || 0)
                          }
                          disabled={!formEnableLate}
                          aria-label="Late grace minutes"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setFormLateMinutes((v) => Math.min(120, v + 1))
                          }
                          disabled={!formEnableLate}
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <p className="help">
                      <strong>0</strong> = no grace; any lateness is flagged.
                      Range: 0–120 minutes.
                    </p>
                  </section>

                  {/* OVERTIME DETECTION */}
                  <section className="settings-card settings-card-wide">
                    <header className="settings-card-head">
                      <div>
                        <h3>Overtime detection</h3>
                        <p className="muted">
                          Decide when hours start counting as overtime for
                          reporting and Payroll.
                        </p>
                      </div>
                    </header>

                    <div className="settings-row compact">
                      <label className="field-label">Overtime based on</label>
                      <div className="pill-toggle">
                        <button
                          type="button"
                          className={
                            formOvertimeBasis === "weekly"
                              ? "pill active"
                              : "pill"
                          }
                          onClick={() => setFormOvertimeBasis("weekly")}
                        >
                          Total hours in period
                        </button>
                        <button
                          type="button"
                          className={
                            formOvertimeBasis === "daily"
                              ? "pill active"
                              : "pill"
                          }
                          onClick={() => setFormOvertimeBasis("daily")}
                        >
                          Daily hours
                        </button>
                      </div>
                    </div>

                    {formOvertimeBasis === "weekly" ? (
                      <div className="settings-row compact">
                        <label className="field-label">
                          Hours before overtime (per pay period)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={1000}
                          step={0.25}
                          value={formPeriodHours}
                          onChange={(e) =>
                            setFormPeriodHours(Number(e.target.value) || 0)
                          }
                          className="control"
                        />
                        <p className="help">
                          Example: <strong>40</strong> for weekly,{" "}
                          <strong>80</strong> for bi-weekly. Payroll uses this
                          to split regular vs overtime hours in each pay period.
                        </p>
                      </div>
                    ) : (
                      <div className="settings-row compact">
                        <label className="field-label">
                          Daily hours before overtime
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={24}
                          step={0.25}
                          value={formDailyHours}
                          onChange={(e) =>
                            setFormDailyHours(Number(e.target.value) || 0)
                          }
                          className="control"
                        />
                        <p className="help">
                          Common setting: <strong>8</strong> hours per day.
                        </p>
                      </div>
                    )}
                  </section>
                </div>

                {/* Footer / actions */}
                <div className="modal-footer">
                  <button
                    className="link-btn"
                    onClick={() => {
                      setFormEnableEarly(true);
                      setFormEarlyMinutes(15);
                      setFormEnableLate(true);
                      setFormLateMinutes(5);
                      setFormOvertimeBasis("weekly");
                      setFormPeriodHours(40);
                      setFormDailyHours(8);
                    }}
                  >
                    Reset to defaults
                  </button>
                  <div className="footer-actions">
                    <button
                      className="ghost-btn"
                      onClick={() => setSettingsOpen(false)}
                    >
                      Cancel
                    </button>
                    <button onClick={saveSettings} disabled={savingSettings}>
                      {savingSettings ? "Saving…" : "Save Settings"}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
